import React, { useState, useEffect } from 'react';
import { ArrowRightLeft, Copy } from 'lucide-react';
import { useTheme } from '../theme';
import { hexToBase64, base64ToHex } from '../lib/hex';
import { logAnalyticsEvent } from '../firebase';

type Mode = 'hex2b64' | 'b642hex';

export default function HexBase64Tool() {
  const { themeClasses } = useTheme();
  const [mode, setMode] = useState<Mode>('hex2b64');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!input.trim()) {
      setOutput('');
      setError('');
      return;
    }
    try {
      setOutput(mode === 'hex2b64' ? hexToBase64(input) : base64ToHex(input));
      setError('');
    } catch (err: any) {
      setError(err?.message || 'Conversion failed');
      setOutput('');
    }
  }, [input, mode]);

  const swap = () => {
    setMode((m) => (m === 'hex2b64' ? 'b642hex' : 'hex2b64'));
    setInput(output && !error ? output : '');
    logAnalyticsEvent('hex_switch_mode', {});
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(output);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      /* ignore */
    }
  };

  const inLabel = mode === 'hex2b64' ? 'Hex input' : 'Base64 input';
  const outLabel = mode === 'hex2b64' ? 'Base64 output' : 'Hex output';
  const placeholder = mode === 'hex2b64' ? 'e.g. 48 65 6c 6c 6f' : 'e.g. SGVsbG8=';

  return (
    <div className={`${themeClasses.cardBg} rounded-3xl border p-5 sm:p-8`}>
      <div className="flex items-center justify-center mb-6">
        <button
          onClick={swap}
          className={`flex items-center gap-2 px-5 py-3 rounded-xl font-semibold ${themeClasses.buttonSecondary} border transition-all hover:scale-105`}
        >
          {mode === 'hex2b64' ? 'Hex → Base64' : 'Base64 → Hex'}
          <ArrowRightLeft size={16} />
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div>
          <label className={`block text-sm font-semibold mb-2 ${themeClasses.textPrimary}`}>{inLabel}</label>
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={placeholder}
            aria-label={inLabel}
            className={`w-full h-40 p-3 ${themeClasses.inputBg} border rounded-xl resize-none break-all`}
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className={`block text-sm font-semibold ${themeClasses.textPrimary}`}>{outLabel}</label>
            {output && (
              <button onClick={copy} className={`flex items-center gap-1 text-sm ${themeClasses.accentText}`}>
                <Copy size={14} /> {copied ? 'Copied!' : 'Copy'}
              </button>
            )}
          </div>
          {error ? (
            <div className={`h-40 p-3 ${themeClasses.errorBg} border rounded-xl`}>{error}</div>
          ) : (
            <textarea
              readOnly
              value={output}
              aria-label={outLabel}
              className={`w-full h-40 p-3 ${themeClasses.inputBg} border rounded-xl resize-none break-all`}
            />
          )}
        </div>
      </div>
    </div>
  );
}
